import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Modal, Row, Col, Tag, Select, Table, Spin, message } from "antd";
import { apiGetShopOrderDetail, apiUpdateShopOrderStatus } from "services/shopOrderService";
import { money, PAYMENT_COLORS, STATUS_COLORS, getStatusLabel, getStatusOptions, isPickupOrder } from "../orderUtils";

const Line = ({ label, children }) =>
  children ? (
    <div style={{ marginBottom: 6 }}>
      <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>{label}:</span>
      <span>{children}</span>
    </div>
  ) : null;

// PayPal never returns the billing address text a card payer entered
// (confirmed against the live API) — only card metadata plus the card
// network's AVS/CVV match result. These decode the raw single-letter codes
// PayPal returns into something readable, keeping the original code visible
// alongside it for anyone who wants to look up the full reference table.
const AVS_LABELS = {
  Y: "Address & postal code match",
  X: "Address & 9-digit zip match",
  D: "Address & postal code match (intl.)",
  M: "Address & postal code match",
  A: "Address matches, postal code doesn't",
  W: "Postal code matches, address doesn't",
  Z: "Postal code matches, address doesn't",
  N: "No match",
  U: "Address info unavailable",
  R: "Retry — issuer system unavailable",
  S: "Not supported by issuer",
  G: "Not supported (international issuer)",
};
const CVV_LABELS = {
  M: "Match",
  N: "No match",
  P: "Not processed",
  S: "Not supported by issuer",
  U: "Issuer unknown",
  X: "No response",
};
const decodeCode = (code, labels) => {
  if (!code) return null;
  const label = labels[code] || "Unrecognized code";
  return `${label} (${code})`;
};

function OrderDetailDialog(props) {
  const { show, proceed, order } = props;
  const [detail, setDetail] = useState(order || {});
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(order?.order_status || "new");
  const [isBusy, setIsBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!order?.id) return;
    setIsBusy(true);
    apiGetShopOrderDetail(order.id)
      .then((data) => {
        setIsBusy(false);
        setDetail(data);
        setItems(Array.isArray(data.items) ? data.items : []);
        setStatus(data.order_status || "new");
      })
      .catch((err) => {
        setIsBusy(false);
        message.error(err);
      });
  }, [order?.id]);

  const currency = detail.currency || "USD";

  const address = [
    detail.address_line,
    detail.city,
    detail.state,
    detail.postal_code,
    detail.country,
  ]
    .filter((x) => x && String(x).trim() !== "")
    .join(", ");

  const itemColumns = [
    { title: "Product", dataIndex: "product_name" },
    { title: "Variant", dataIndex: "variant_name", align: "center" },
    { title: "SKU", dataIndex: "sku", align: "center" },
    {
      title: "Unit",
      dataIndex: "unit_price",
      align: "right",
      render: (t) => money(t, currency),
    },
    { title: "Qty", dataIndex: "qty", align: "center" },
    {
      title: "Total",
      dataIndex: "line_total",
      align: "right",
      render: (t) => money(t, currency),
    },
  ];

  const onSaveStatus = () => {
    setSaving(true);
    apiUpdateShopOrderStatus(detail.id, status)
      .then(() => {
        setSaving(false);
        message.success(
          status === "ready_for_pickup"
            ? "Order status updated — the customer has been emailed"
            : "Order status updated"
        );
        proceed({ order_status: status });
      })
      .catch((err) => {
        setSaving(false);
        message.error(err);
      });
  };

  const pickup = isPickupOrder(detail);

  return (
    <Modal
      open={show}
      width={780}
      destroyOnClose={true}
      maskClosable={false}
      title={`Order ${detail.order_no || ""}`}
      onCancel={() => proceed(null)}
      okText="Save status"
      okButtonProps={{ loading: saving }}
      onOk={onSaveStatus}
    >
      <Spin spinning={isBusy}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <h4>Customer</h4>
            <Line label="Name">{detail.customer_name}</Line>
            <Line label="Email">{detail.customer_email}</Line>
            <Line label="Phone">{detail.customer_phone}</Line>
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>Delivery:</span>
              <Tag color={pickup ? "gold" : detail.delivery_method === "international" ? "purple" : "default"}>
                {detail.shipping_method_label || (pickup ? "Customer Pickup" : "Thailand Delivery")}
              </Tag>
            </div>
            <Line label={pickup ? "Billing address" : "Delivery address"}>{address}</Line>
            <Line label="Note">{detail.note}</Line>
          </Col>
          <Col xs={24} md={12}>
            <h4>Payment</h4>
            <Line label="Method">{detail.payment_method}</Line>
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: "var(--darkGreyColor)", marginRight: 8 }}>Payment:</span>
              <Tag color={PAYMENT_COLORS[detail.payment_status] || "default"}>
                {detail.payment_status || "-"}
              </Tag>
            </div>
            <Line label="PayPal Order">{detail.paypal_order_id}</Line>
            <Line label="PayPal Capture">{detail.paypal_capture_id}</Line>
            <Line label="Payer">{detail.payer_email}</Line>
            {detail.paypal_card_brand && (
              <>
                <div style={{ marginTop: 10, marginBottom: 4, color: "var(--darkGreyColor)", fontSize: 12, fontWeight: 600 }}>
                  PAYPAL BILLING VERIFICATION
                </div>
                <Line label="Card">
                  {detail.paypal_card_brand} •••• {detail.paypal_card_last4}
                </Line>
                <Line label="Address match (AVS)">{decodeCode(detail.paypal_avs_code, AVS_LABELS)}</Line>
                <Line label="CVV match">{decodeCode(detail.paypal_cvv_code, CVV_LABELS)}</Line>
              </>
            )}
            <Line label="Placed">{detail.add_timestamp}</Line>
          </Col>
        </Row>

        <div style={{ marginTop: 12 }}>
          <Table
            size="small"
            pagination={false}
            rowKey={(r) => r.id}
            columns={itemColumns}
            dataSource={items}
          />
          <div style={{ textAlign: "right", marginTop: 12 }}>
            <div style={{ fontSize: 13, color: "var(--darkGreyColor)" }}>
              Products: {money(detail.subtotal, currency)}
            </div>
            <div style={{ fontSize: 13, color: "var(--darkGreyColor)" }}>
              {detail.shipping_method_label || (pickup ? "Customer Pickup" : "Shipping")}:{" "}
              {Number(detail.shipping_amount) > 0 ? money(detail.shipping_amount, currency) : "FREE"}
            </div>
            {detail.delivery_method === "international" && detail.epacket_plus === "1" && (
              <div style={{ fontSize: 12, color: "var(--darkGreyColor)" }}>+ Protection surcharge included</div>
            )}
            <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
              Total: {money(detail.total, currency)}
            </div>
          </div>
        </div>

        <Row align="middle" style={{ marginTop: 16 }} gutter={12}>
          <Col>
            <span style={{ color: "var(--darkGreyColor)" }}>Current status:</span>
          </Col>
          <Col>
            <Tag color={STATUS_COLORS[detail.order_status] || "default"}>
              {getStatusLabel(detail)}
            </Tag>
          </Col>
          <Col>
            <span style={{ color: "var(--darkGreyColor)", marginLeft: 12 }}>Set to:</span>
          </Col>
          <Col>
            <Select
              style={{ width: 190 }}
              value={status}
              onChange={setStatus}
              options={getStatusOptions(detail)}
            />
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
}

export default confirmable(OrderDetailDialog);
